import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { X, CheckCircle2, AlertTriangle } from 'lucide-react';

type NotificationType = 'success' | 'error';

interface Notification {
  id: number;
  type: NotificationType;
  title: string;
  description?: string;
  createdAt: Date;
}

interface NotificationContextValue {
  notifySuccess: (title: string, description?: string) => void;
  notifyError: (title: string, description?: string) => void;
  notifyApiSuccess: (entity: string, action: string, extraDetails?: string) => void;
  notifyApiError: (entity: string, action: string, errorMessage?: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined
);

const AUTO_DISMISS_MS = 5000;

interface NotificationContainerProps {
  notifications: Notification[];
  onClose: (id: number) => void;
}

const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  onClose,
}) => {
  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex w-full max-w-sm flex-col space-y-3">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className="overflow-hidden rounded-lg border bg-background shadow-lg"
        >
          <Alert
            variant={notification.type === 'error' ? 'destructive' : 'default'}
            className="relative pr-10"
          >
            {notification.type === 'success' ? (
              <CheckCircle2 className="text-verified-green" />
            ) : (
              <AlertTriangle className="text-destructive" />
            )}
            <AlertTitle>{notification.title}</AlertTitle>
            {(notification.description || notification.createdAt) && (
              <AlertDescription>
                {notification.description && <p>{notification.description}</p>}
                <p className="mt-1 text-xs text-muted-foreground">
                  {notification.createdAt.toLocaleTimeString()}
                </p>
              </AlertDescription>
            )}
            <button
              type="button"
              onClick={() => onClose(notification.id)}
              className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground hover:bg-muted"
              aria-label="Dismiss notification"
            >
              <X className="h-3 w-3" />
            </button>
          </Alert>
        </div>
      ))}
    </div>
  );
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const addNotification = useCallback(
    (type: NotificationType, title: string, description?: string) => {
      const id = Date.now() + Math.random();
      const createdAt = new Date();

      setNotifications((prev) => [
        ...prev,
        { id, type, title, description, createdAt },
      ]);

      window.setTimeout(() => {
        removeNotification(id);
      }, AUTO_DISMISS_MS);
    },
    [removeNotification]
  );

  const notifySuccess = useCallback(
    (title: string, description?: string) =>
      addNotification('success', title, description),
    [addNotification]
  );

  const notifyError = useCallback(
    (title: string, description?: string) =>
      addNotification('error', title, description),
    [addNotification]
  );

  const notifyApiSuccess = useCallback(
    (entity: string, action: string, extraDetails?: string) => {
      const title = `${entity} ${action} successfully`;
      notifySuccess(title, extraDetails);
    },
    [notifySuccess]
  );

  const notifyApiError = useCallback(
    (entity: string, action: string, errorMessage?: string) => {
      const title = `Failed to ${action} ${entity.toLowerCase()}`;
      const description = errorMessage || 'Please try again.';
      notifyError(title, description);
    },
    [notifyError]
  );

  const value: NotificationContextValue = {
    notifySuccess,
    notifyError,
    notifyApiSuccess,
    notifyApiError,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer
        notifications={notifications}
        onClose={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

export function useNotification(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}



